import { initializeTableV2Presence } from '../bootstrap/initialize-table-v2-presence.ts';
import { createApplicationLayoutComposition } from '../layouts/create-application-layout-composition.ts';
// dependency-graph-ignore-file
window.global ||= window; // Ensures that `global` is defined in the browser for libraries that require it.

import {
  currentBridgeData,
  initializeBridgeData,
} from '@databases/database-state/state/bridge-data';
import { mountDefaultFlagGroup } from '@databases/flags';
import { getAppModeFromQueryParams } from '@databases/modes';
import {
  initNativeEmbedEditorPlugin,
  sendContentReady,
} from '@databases/native-embed-editor-plugin';
import { container } from '@databases/service-container';
import React from 'react';

import { createBridgeDatabaseLoadDispatchRecyclable } from '../bootstrap/bridge-identity/create-bridge-database-load-dispatch-recyclable.ts';
import { createClearBridgeFlagsRecyclable } from '../bootstrap/bridge-identity/create-clear-bridge-flags-recyclable.ts';
import { createCloseConfluenceSidePanelRecyclable } from '../bootstrap/bridge-identity/create-close-confluence-side-panel-recyclable.ts';
import { createPublishEmptyDatabaseReferencingRecyclable } from '../bootstrap/bridge-identity/create-publish-empty-database-referencing-recyclable.ts';
import { tryDispatchDatabaseLoadFromCurrentBridge } from '../bootstrap/bridge-identity/try-dispatch-database-load-from-current-bridge.ts';
import { trySdkDatabaseLoadFromCurrentBridge } from '../bootstrap/bridge-identity/try-sdk-database-load-from-current-bridge.ts';
import { setupCommentsPanelBridgeListeners } from '../bootstrap/comments-panel-listener.ts';
import { createFirstEditAnalyticsRecyclable } from '../bootstrap/create-first-edit-analytics-recyclable.ts';
import { getModeConfig } from '../bootstrap/get-mode-config.ts';
import { initializeCurrentDatabaseSdk } from '../bootstrap/initialize-current-database-sdk.ts';
import { getThemeStateFromQueryParams } from '../bootstrap/initialize-database-theme.ts';
import { initializeDatabasesRovoBridge } from '../bootstrap/initialize-databases-rovo-bridge.ts';
import { initializePermissionsManager } from '../bootstrap/initialize-permissions-manager.ts';
import { initializeRecyclableDatabasePaywallFeatures } from '../bootstrap/initialize-recyclable-database-paywall-features.ts';
import { initializeRecyclableSelectionsMachine } from '../bootstrap/initialize-recyclable-selections-machine.ts';
import { initializeTableHighlightsController } from '../bootstrap/initialize-table-highlights-controller.ts';
import { preloadToolbar } from '../bootstrap/preload-toolbar.ts';
import { registerPanelsForPanelRenderer } from '../bootstrap/register-panels-for-panel-renderer.tsx';
import { registerRecyclableModules } from '../bootstrap/register-recyclable-modules.ts';
import { resolveSdkDatabaseLoad } from '../bootstrap/resolve-sdk-database-load.ts';

import { tryInitializeNativeEmbedBridge } from './bridge/try-initialize-native-embed-bridge.ts';
import AppWithLocale from './components/app-with-locale.tsx';
import { createDatabasesApp } from './create-databases-app.tsx';

// Loading waterfall:
//
//   initializeBridgeData()        — resolves cloudId, contentId, token from bridge
//         │
//         ├── sdkLoadPromise      — registered here, but the WebSocket connect is
//         │                         internally gated on `featureFlagsInitialized`
//         │                         so we route to the correct collab URL. This
//         │                         still overlaps most of intl fetching.
//         │
//         └── bootstrap()         — loads intl messages + feature flags (network-bound)
//                │
//                └── resolveSdkDatabaseLoad()  — awaits the SDK promise, injects the
//                                               loaded database directly into Redux
//                                               (skipping PENDING state)
//
// If the SDK loader fails for any reason, we fall back to the legacy Redux thunk
// path which goes through the full PENDING → SUCCEEDED loading state lifecycle.

const run = createDatabasesApp(async ({ bootstrap, mount }) => {
  const { cloudId, spaceId, identity, contentId, referencing } =
    await initializeBridgeData();

  // Kick off the SDK collab load. Internally it awaits feature flags before
  // opening the socket (the collab URL is flag-gated), then races the WebSocket
  // handshake against the remaining bootstrap work.
  const sdkLoadPromise = trySdkDatabaseLoadFromCurrentBridge();

  const appMode = getAppModeFromQueryParams(new URLSearchParams(window.location.search));
  const modeConfig = getModeConfig(appMode);

  await bootstrap({
    appMode,
    cloudId,
    identity,
    dependencies: modeConfig.dependencies,
    referencing,
    spaceId,
    themeState: getThemeStateFromQueryParams(),
  });

  // Resolve the SDK load now that bootstrap has finished (intl is available for
  // migrations). On success, the database is injected into Redux ready-to-render.
  const sdkLoadSucceeded = sdkLoadPromise
    ? await resolveSdkDatabaseLoad(sdkLoadPromise, {
        contentId: currentBridgeData.data.contentId,
        contentReferenceId: currentBridgeData.data.contentReferenceId,
        spaceId: currentBridgeData.data.spaceId,
        spaceKey: currentBridgeData.data.spaceKey,
        spaceName: currentBridgeData.data.spaceName,
        title: currentBridgeData.data.title,
      })
    : false;

  if (!sdkLoadSucceeded) {
    tryDispatchDatabaseLoadFromCurrentBridge();
  }

  preloadToolbar();
  registerPanelsForPanelRenderer();

  if (appMode === 'NATIVE_EMBED') {
    await tryInitializeNativeEmbedBridge(() => currentBridgeData.data);
  }

  if (appMode === 'NATIVE_EMBED_EDITOR_PLUGIN') {
    initNativeEmbedEditorPlugin();

    container.resolve('EventBus').on('databaseUILoaded', () => {
      sendContentReady();
    });
  }

  const layoutComposition = createApplicationLayoutComposition();

  registerRecyclableModules(
    [
      createBridgeDatabaseLoadDispatchRecyclable(),
      createFirstEditAnalyticsRecyclable(),
      createClearBridgeFlagsRecyclable(),
      createCloseConfluenceSidePanelRecyclable(),
      createPublishEmptyDatabaseReferencingRecyclable(),
      initializeCurrentDatabaseSdk(),
      initializeDatabasesRovoBridge(),
      initializePermissionsManager(),
      initializeRecyclableDatabasePaywallFeatures(),
      setupCommentsPanelBridgeListeners(),
      layoutComposition.tableV2Lifecycle,
      initializeTableHighlightsController(layoutComposition.readIdentity),
      initializeTableV2Presence(layoutComposition.getPresenceDependencies),
      initializeRecyclableSelectionsMachine(layoutComposition.getSelectionDependencies),
    ],
    contentId,
    layoutComposition.readIdentity,
  );

  if (
    process.env.MODE === 'test' &&
    globalThis.databasePerformanceBenchmarkEnabled === true
  ) {
    const { installPerformanceBenchmarkAdapter } =
      await import('../performance-benchmark/install-performance-benchmark-adapter.ts');
    await installPerformanceBenchmarkAdapter();
  }

  mount((getRoot) => {
    const root = getRoot();

    root.render(<AppWithLocale tableV2Host={layoutComposition.tableV2Host} />);

    mountDefaultFlagGroup(() => container.resolve('FlagsManager'));
  });
});

run();
